import React, { useEffect, useId, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Order } from "../../../../../services";
import { useWagonTypes } from "../../../../../hooks/useWagonTypes";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import { hasCompleteWagonNumbers } from "./wagonNumbersValidation";
import style from "./WagonNumbersBlock.module.scss";

const prefix = "orderInfoExpeditor.wagonNumbers";

const WagonNumbersBlock = ({ order, onSaved, onSavingChange, disabled = false, validationRequest = 0 }) => {
  const { t, i18n } = useTranslation();
  const count = Number(order.wagonsCount);
  const titleId = useId();
  const { options } = useWagonTypes(true, i18n?.language || "ru");
  const wagonType = options.find((type) => type.id === order.wagonTypeId)?.name || "—";
  const savedNumbersKey = JSON.stringify(order.wagonNumbers || []);
  const [numbers, setNumbers] = useState(() =>
    Array.from({ length: count }, (_, index) => order.wagonNumbers?.[index] || ""),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const blockRef = useRef(null);
  const fieldsRef = useRef(null);
  const mountedRef = useRef(false);
  const savingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const savedNumbers = JSON.parse(savedNumbersKey);
    setNumbers(Array.from({ length: count }, (_, index) => savedNumbers[index] || ""));
  }, [count, savedNumbersKey]);

  useEffect(() => {
    if (!validationRequest) return;
    blockRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    blockRef.current?.querySelector(count === 1 ? "input" : "button")?.focus({ preventScroll: true });
  }, [validationRequest, count]);

  const canSave = hasCompleteWagonNumbers(count, numbers);
  const hasSavedNumbers = hasCompleteWagonNumbers(count, order.wagonNumbers);
  const showErrors = hasAttemptedSave || validationRequest > 0;
  const singleError =
    showErrors && !numbers[0]?.trim()
      ? t(`${prefix}.placeholder`)
      : validationRequest > 0 && !hasSavedNumbers
        ? t(`${prefix}.saveRequired`)
        : "";
  const savedCount = (order.wagonNumbers || []).filter(
    (number) => typeof number === "string" && number.trim(),
  ).length;
  const changeNumber = (index, value) => {
    setNumbers((previous) => previous.map((number, position) => (position === index ? value : number)));
  };
  const closeModal = () => {
    if (!savingRef.current) setIsOpen(false);
  };

  const saveNumbers = async () => {
    if (savingRef.current || disabled || !order.id) return;
    if (!canSave) {
      setHasAttemptedSave(true);
      toast.error(t(`${prefix}.fillAll`));
      const fieldContainer = count === 1 ? blockRef.current : fieldsRef.current;
      const firstEmptyInput = Array.from(fieldContainer?.querySelectorAll("input") || []).find(
        (input) => !input.value.trim(),
      );
      firstEmptyInput?.focus();
      return;
    }
    const wagonNumbers = numbers.map((number) => number.trim());
    savingRef.current = true;
    setIsSaving(true);
    onSavingChange(true);
    try {
      await Order.saveWagonNumbers(order.id, wagonNumbers);
    } catch {
      if (mountedRef.current) toast.error(t(`${prefix}.saveError`));
      return;
    } finally {
      savingRef.current = false;
      if (mountedRef.current) {
        setIsSaving(false);
        onSavingChange(false);
      }
    }
    onSaved(order.id, wagonNumbers);
    if (mountedRef.current) {
      setNumbers(wagonNumbers);
      setIsOpen(false);
      setHasAttemptedSave(false);
      toast.success(t(`${prefix}.saved`));
    }
  };

  return (
    <div className={style.block} ref={blockRef}>
      <div className={style.title}>{t(`${prefix}.${count === 1 ? "titleSingle" : "titleMultiple"}`)}</div>
      {count === 1 ? (
        <div className={style.singleRow}>
          <CustomInput
            labelText={t(`${prefix}.singleLabel`, { type: wagonType })}
            placeholder={t(`${prefix}.placeholder`)}
            inputType="number"
            isDigitsOnly
            isBgGray
            value={numbers[0]}
            onChangeValue={(value) => changeNumber(0, value)}
            isDisabled={isSaving || disabled}
            errorText={singleError}
          />
          <CustomButton
            type="button"
            text={t(`${prefix}.saveSingle`)}
            isNavy
            isLoading={isSaving}
            disabled={isSaving || disabled}
            onClick={saveNumbers}
          />
        </div>
      ) : (
        <div className={style.summary}>
          <CustomInput
            labelText={t(`${prefix}.multipleLabel`, { type: wagonType })}
            value={t(`${prefix}.summary`, { saved: savedCount, total: count })}
            isBgGray
            isReadOnly
            isDisabled={isSaving || disabled}
            errorText={showErrors && !hasSavedNumbers ? t(`${prefix}.required`) : ""}
          />
          <CustomButton
            type="button"
            text={t(`${prefix}.${hasSavedNumbers ? "edit" : "enter"}`)}
            isNavy
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          />
        </div>
      )}
      <Modal
        show={isOpen}
        onHide={closeModal}
        centered
        scrollable
        backdrop={isSaving ? "static" : true}
        keyboard={!isSaving}
        className={style.modal}
        dialogClassName={style.dialog}
        contentClassName={style.content}
        aria-labelledby={titleId}
        data-portal-modal="true"
      >
        <div className={style.header}>
          <h2 id={titleId}>{t(`${prefix}.modalTitle`)}</h2>
          <button
            type="button"
            className={style.close}
            onClick={closeModal}
            disabled={isSaving}
            aria-label={t("orderInfoExpeditor.assignDriverModal.close")}
          >
            <CloseIconNew width="24" height="24" fill="#6D7885" />
          </button>
          <p>{t(`${prefix}.modalSubtitle`)}</p>
          <p>{t(`${prefix}.typeAndCount`, { type: wagonType, count })}</p>
        </div>
        <Modal.Body className={style.fields} ref={fieldsRef}>
          {numbers.map((number, index) => (
            <CustomInput
              key={index}
              labelText={t(`${prefix}.wagonLabel`, { number: index + 1 })}
              placeholder={t(`${prefix}.placeholder`)}
              inputType="number"
              isDigitsOnly
              isBgGray
              value={number}
              onChangeValue={(value) => changeNumber(index, value)}
              isDisabled={isSaving || disabled}
              errorText={showErrors && !number.trim() ? t(`${prefix}.placeholder`) : ""}
            />
          ))}
        </Modal.Body>
        <div className={style.footer}>
          <CustomButton
            type="button"
            text={t("common.cancel")}
            isNavy
            isWhite
            onClick={closeModal}
            disabled={isSaving}
          />
          <CustomButton
            type="button"
            text={t(`${prefix}.saveMultiple`)}
            isNavy
            onClick={saveNumbers}
            isLoading={isSaving}
            disabled={isSaving || disabled}
          />
        </div>
      </Modal>
    </div>
  );
};

export default WagonNumbersBlock;
